import { ReactNode, useCallback, useLayoutEffect, useMemo, useRef, useState } from "react"
import { useIntl } from "react-intl"
import styled from "styled-components"
import { Button, Window, HeadingBold, Icon, TertiaryCtaButton, WindowState, SecondaryCtaButton, ModelViewer, InvertTheme, Swatch } from "slate-ui-components"
import { useAnalytics } from "../AnalyticsProvider"
import { useAppDispatch, useAppSelector } from "../store/hooks"
import { useImageMedia } from "../utils/useImageMedia"
import { optionToggle } from "../store/reducers/visualizer"
import { LoadingOverlay } from "../LoadingOverlay"


interface ItemDetailsDialog {
	open: () => void
	close: () => void
	element: ReactNode
}

const CloseButton = styled(Button)`
	align-self: flex-end;
	margin: -15px;
	padding: 15px;
	--icon-foreground: ${({theme}) => theme.colours.primary.normal};
	&:hover, &:focus {
		--icon-foreground: ${({theme}) => theme.colours.primary.hover};
	}
`

const Heading = styled(HeadingBold)`
	max-width: 390px;
	padding-bottom: 10px;
`

const ScrollContainer = styled.div`
	position: relative;
	display: flex;
	flex: 1 1 auto;
	overflow: hidden;
`
const Scroll = styled.div`
	display: flex;
	flex-flow: column nowrap;
	overflow-y: auto;
	margin: 0 -8px;
	${({theme}) => theme.components.scrollBar};
`
const ScrollContents = styled.div`
	padding: 0 8px;
`

const HeroPlaceholder = styled.div`
	width: 100%;
	max-width: 50vh;
	aspect-ratio: 1;
	background-color: ${({theme}) => theme.colours.background.contrast};
	pointer-events: all;
	isolation: isolate;
	z-index: 100;
	flex-basis: 0;
	position: relative;
`

const HeroImage = styled.img`
	width: 100%;
	height: 100%;
	object-fit: cover !important;
	object-position: center !important;
	display: block;
`

const Description = styled.p`
	width: 100%;
	max-width: 390px;
	${({theme}) => theme.fonts.body2.normal};
`

const Gradient = styled.div<{$scrolled: boolean | undefined}>`
	position: absolute;
	bottom: -1px;
	width: 100%;
	height: 2em;
	background: ${({theme}) => theme.colours.gradient.normal};
	opacity: ${({$scrolled}) => $scrolled === false ? 1 : 0};
	transition: opacity 0.1s;
`

const Overlay = styled.div`
	position: absolute;
	inset: 0;
`

export const useItemDetailsDialog = (groupId: string | undefined, itemId: string | undefined, from: string) : ItemDetailsDialog => {
	const intl = useIntl()
	const {record} = useAnalytics()
	const [windowState, updateWindowState] = useState<WindowState>("closed")
	const dispatch = useAppDispatch();
	const item = useAppSelector(s => !!groupId && !!itemId && s.configuration.currentVisualizerConfig.configuration.optionsGroups[groupId].options[itemId] || undefined) 
	const selected = useAppSelector(s => !!groupId && !!itemId && s.configuration.selectedOptions[groupId].includes(itemId));
	const thumbnail = useImageMedia(item?.thumbnailPreview);
	const proxy = item?.proxyModel;
	const scrollable = useRef<HTMLDivElement>(null);
	const [scrolled, setScrolled] = useState<boolean | undefined>(undefined)

	const open = useCallback(() => {
		updateWindowState(prevState => prevState !== "open" ? "opening" : "open")
		record?.(["Configurator", "ItemDetailsOpen"], {from, item: itemId})
	}, [record, from, itemId])
	
	const close = useCallback(() => {
		updateWindowState(prevState => prevState !== "closed" ? "closing" : "closed")
		record?.(["Configurator", "ItemDetailsClose"])
	}, [record])

	useLayoutEffect(() => {
		const scrollEl = scrollable.current
		if(scrollEl) {
			setScrolled(scrollEl.clientHeight !== scrollEl.scrollHeight ? scrollEl.scrollTop !== 0 : undefined)
			const onScroll = () => {
				setScrolled(scrollEl.clientHeight !== scrollEl.scrollHeight ? scrollEl.scrollTop !== 0 : undefined)
			}
			scrollEl.addEventListener("scroll", onScroll)
			return () => {
				scrollEl.removeEventListener("scroll", onScroll)
			}
		}
		else {
			setScrolled(undefined)
		}
		return;
	}, [setScrolled])

	const element = useMemo(() => {
		if(windowState === "closed") {
			return null;
		}

		if(!item || !groupId || !itemId) {
			return null;
		}

		const name = intl.formatMessage(item.displayName)

		let hero = null;
		if(thumbnail?.url) {
			hero = <HeroImage src={thumbnail.url} role="presentation"/>
		}
		else if(item.thumbnail.type === "swatch") {
			hero = <Swatch colour={item.thumbnail.colour} finish={item.thumbnail.finish}/>
		}

		if(proxy?.glb) {
			hero = <ModelViewer envFilepath="/proxy_environment.env" glbFilepath={proxy.glb} usdzFilepath={proxy.usdz} alt={name} placeholder={<Overlay>{hero}<LoadingOverlay busy/></Overlay>} />
		}

		return (
			<InvertTheme>
				<Window state={windowState} updateState={updateWindowState} title={name}>
					<CloseButton label={intl.formatMessage({id: "general.close", defaultMessage: "Close"})} testId="item-details.close" onClick={close}>
						<Icon icon="Close"/>
					</CloseButton>
					<Heading>
						{intl.formatMessage(item.displayName)}
					</Heading>
					<ScrollContainer>
						<Scroll ref={scrollable}>
							<ScrollContents>
								<HeroPlaceholder onClick={e => e.stopPropagation()}>
									{hero}
								</HeroPlaceholder>
								<Description>{item.description && intl.formatMessage(item.description)}</Description>
							</ScrollContents>
						</Scroll>
						<Gradient $scrolled={scrolled}/>
					</ScrollContainer>
					{!item.buildSessionUpdate ?
						selected ?
							<SecondaryCtaButton label={intl.formatMessage({id: "general.remove", defaultMessage: "REMOVE"})} onClick={() => dispatch(optionToggle({group: groupId, option: itemId}))} testId="item-details.remove"/>
							:
							<TertiaryCtaButton label={intl.formatMessage({id: "general.add", defaultMessage: "ADD"})} onClick={() => dispatch(optionToggle({group: groupId, option: itemId}))} testId="item-details.add"/>
						:
						null
					}
				</Window>
			</InvertTheme>
		)
	}, [close, dispatch, groupId, intl, item, itemId, proxy?.glb, proxy?.usdz, scrolled, selected, thumbnail.url, windowState])

	return {
		open,
		close,
		element
	}
}